import { faDoorOpen } from '@fortawesome/free-solid-svg-icons'
import classNames from 'classnames'
import React, { useState } from "react"
import { useDispatch } from 'react-redux'
import { SetupSelectBox } from 'web/components/generic/SetupSelectBox'
import { v4 } from 'uuid'
import { FightSegmentList } from 'web/components/fight-segments/FightSegmentList'
import { FaButton } from 'web/components/generic/FaButton'
import { FightSetup } from 'web/data/models'
import { EditSetupActions, useEditSetupState } from 'web/redux/slices/edit-setup-slice'
import { useFightSelectors } from 'web/redux/slices/fight-slice'
import { InteractivityActions, useInteractivitySelectors } from 'web/redux/slices/interactivity-slice'
import { useSetupState } from 'web/redux/slices/setup-slice'
import { decorateNameWithOrdinal } from 'web/utils/text-utils'
import { assertDefined, expectOneById, sortByDateCreated, splitList, tryGetOneById } from 'web/utils/utils'
import { createEventAssignmentsCallback } from '../shared/utils'

export const EditSetupComparisonContainer: React.FC = () => {

    const disp = useDispatch()
    const interactivitySelectors = useInteractivitySelectors()

    const setupState = useSetupState()
    const editSetupState = useEditSetupState()

    const setupBeingEdited = editSetupState.setup
    assertDefined(setupBeingEdited)

    const { taken } = splitList(
        setupState.setups,
        (item) => {
            return (
                item.fightId === setupBeingEdited.fightId &&
                item.id !== setupBeingEdited.id
            )
        }
    )

    // Find the current setup in the setup store and create a copy of
    // it with a different name and ID. This copy will allow us to
    // compare the currently edited setup with itself, as it was
    // before we started editing it.
    //
    // The ID reroll is important in order for the highlighting to
    // work correctly.
    //
    // If the setup being edited isn't saved, all this is moot and we
    // just return undefined.
    const [setupBeingEditedCopy] = useState(() => {
        const storedSetupBeingEdited = tryGetOneById(
            setupState.setups, setupBeingEdited.id
        )

        if (storedSetupBeingEdited == null) {
            return undefined
        }

        return {
            ...storedSetupBeingEdited,
            id: v4(),
            name: `(currently editing)`
        }
    })

    const availableSetups: FightSetup[] = []

    if (setupBeingEditedCopy != null) {
        availableSetups.push(setupBeingEditedCopy)
    }

    availableSetups.push(...sortByDateCreated(taken))

    const [selectedSetupId, setSelectedSetupId] = useState<string>(availableSetups[0].id)

    const setup = expectOneById(availableSetups, selectedSetupId)

    const { getFullFight } = useFightSelectors()
    const fullFight = getFullFight(setupBeingEdited.fightId, setup.setupFightEvents)

    const eventAssignmentsCallback = createEventAssignmentsCallback({
        fullFight: fullFight,
        selectedSetup: setup,
        onHoverEnter: (({ setupId, availableEventIds }) => {
            disp(InteractivityActions.setAvailableEventIdsForSetup({
                setupId,
                availableEventIds
            }))
        }),
        onHoverLeave: ({ setupId }) => {
            disp(InteractivityActions
                .clearAvailableEventIdsForSetup({ setupId }))
        }
    })

    return (
        <div className="pr-3" style={{ width: "650px", marginTop: "68px" }}>

            <div className={classNames(
                "d-flex p-2 bg-light rounded border mb-3",
                "align-items-end justify-content-between",
                "space-mx-2 small"
            )}>
                <SetupSelectBox
                    input={{
                        bsSize: "sm"
                    }}
                    select={{
                        disabled: false,
                        selectedSetup: selectedSetupId,
                        setups: availableSetups,
                        onSelected(option) {
                            setSelectedSetupId(option.id)
                        },
                    }}
                />

                <FaButton
                    icon={faDoorOpen}
                    size="sm"
                    outline
                    color="danger"
                    className="flex-shrink-0"
                    onClick={() => {
                        disp(EditSetupActions.toggleComparison({ comparisonActive: false }))
                    }}
                >
                    {"Close comparison"}
                </FaButton>
            </div>

            <div className="space-my-3">
                <FightSegmentList
                    segments={fullFight.segments}
                    events={fullFight.events}
                    segmentProps={{
                        decorateNameWithOrdinal: (event) => {
                            return decorateNameWithOrdinal(fullFight.events, event)
                        },

                        eventProps: {
                            interactive: {
                                extraClassNames: (eventId: string) => {
                                    const eventState = interactivitySelectors
                                        .getEventStateForSetup(setup.id, eventId)

                                    if (eventState.valid) {
                                        return "bg-light-green"
                                    } else if (eventState.invalid) {
                                        return "bg-light-red"
                                    }
                                }
                            },
                            assignments: {
                                eventAssignments: eventAssignmentsCallback
                            }
                        }
                    }}
                />
            </div>
        </div>
    )
}
